<template>
  <tbody>
    <template v-if="!isUnitManagerView">
      <tr>
        <td class="border-bottom">
          <div class="d-flex flex-row-reverse">{{ $t('gridView.marginLever') }}</div>
        </td>
        <td :colspan="getColspan(9)" class="border-bottom" />
        <td v-if="!isAgGridProductListEnabled" class="alert-cell" />
      </tr>
      <grid-view-scenario-selector
        v-for="scenarioAggregation in scenarioAggregations"
        :key="scenarioAggregation._id"
        :scenario="scenarioAggregation"
        :level-entry-key="levelEntryKey"
        @updateDefaultScenario="updateDefaultScenario"
      />
    </template>

    <tr class="add-scenario-row">
      <td :class="canAddScenario ? 'pl-0' : 'pl-3'">
        <div class="d-flex align-center">
          <v-btn
            v-if="canAddScenario"
            color="primary"
            small
            text
            class="pl-0 mr-2"
            @click="showAddScenarioDialog"
          >
            <v-icon v-once class="pl-0">add_box</v-icon>
            <span class="add-button ml-1 pl-0">
              {{ $t('gridView.addScenario.addScenarioButton') }}
            </span>
          </v-btn>
          <v-btn
            id="download-button"
            :loading="downloadingItems"
            color="primary"
            small
            depressed
            @click="downloadCurrentView"
          >
            {{ $t('actions.download') }}
          </v-btn>
        </div>
      </td>
      <td :colspan="getColspan(9)" class="blank-cell" />
      <td v-if="!isAgGridProductListEnabled" class="alert-cell" />
    </tr>

    <tr>
      <td :colspan="getColspan(11)">
        <grid-view-container
          ref="gridViewContainer"
          :is-unit-manager-view="isUnitManagerView"
          :level-entry-key="levelEntryKey"
          @toggleFilteringByArchitectureGroup="onToggleFilteringByArchitectureGroup"
        />
      </td>
    </tr>
  </tbody>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { find } from 'lodash';
import { candidate } from '@enums/scenario-version';
import { enablePriceOptimisation } from '@enums/feature-flags';
import featureFlagsMixin from '../../../mixins/featureFlags';

export default {
  mixins: [featureFlagsMixin],

  props: {
    levelEntryKey: String,
    isUnitManagerView: Boolean,
  },

  data() {
    return {};
  },

  computed: {
    ...mapState('scenarioMetadata', {
      scenarioMetadata: state => state.scenarioMetadata,
      aggregatedScenarios: state => state.aggregatedScenarios,
    }),
    ...mapGetters('context', ['isWholesaleManager', 'isWholesaleAnalyst', 'isAdminLoggedIn']),
    ...mapGetters('gridView', ['getColspan', 'isAgGridProductListEnabled']),
    ...mapState('gridView', ['downloadingItems']),

    candidateScenario() {
      return this.scenarioMetadata.find(sc => sc.version === candidate);
    },

    scenarioAggregations() {
      return this.scenarioMetadata.map(sc => {
        const matchedScenario = find(this.aggregatedScenarios, { scenarioKey: sc.scenarioKey });
        return { ...matchedScenario, ...sc };
      });
    },

    canAddScenario() {
      // Users must have permission AND not be in Unit Manager view (articles by impact)
      const hasPermission =
        this.isAdminLoggedIn || (!this.isWholesaleManager && !this.isWholesaleAnalyst);
      return hasPermission && !this.isUnitManagerView;
    },
  },

  async created() {
    if (!this.isUnitManagerView) {
      await this.refetchScenarioMetadata();
      this.getAggregatedScenarios();
      this.checkPricingEngineStatus(this.candidateScenario.scenarioKey);

      if (this.isFeatureFlagEnabled(enablePriceOptimisation)) {
        this.checkCrossElasticityStatus(this.candidateScenario.scenarioKey);
      }
    }
  },

  methods: {
    ...mapActions('scenarioMetadata', [
      'fetchScenarioMetadata',
      'electNewCandidateScenario',
      'getAggregatedScenarios',
    ]),
    ...mapActions('gridView', [
      'fetchItems',
      'checkPricingEngineStatus',
      'checkCrossElasticityStatus',
    ]),
    ...mapActions('filters', ['setSelectedFilter']),

    async updateDefaultScenario(newCandidateScenarioKey) {
      const oldCandidateScenarioKey = this.candidateScenario.scenarioKey;
      const pricingGroupId = this.candidateScenario.hierarchyId;
      await this.electNewCandidateScenario({
        newCandidateScenarioKey,
        pricingGroupId,
        oldCandidateScenarioKey,
      });
      await this.refetchScenarioMetadata();
      await this.setSelectedFilter({
        filterName: 'scenario',
        filterValue: newCandidateScenarioKey,
      });
      this.checkPricingEngineStatus();
      if (this.isFeatureFlagEnabled(enablePriceOptimisation)) {
        this.checkCrossElasticityStatus();
      }
      this.globalEmit('new-candidate-scenario-elected');
      await this.fetchItems({ levelEntryKey: this.levelEntryKey });
    },

    async refetchScenarioMetadata() {
      const params = {
        where: { hierarchyId: this.levelEntryKey },
      };
      await this.fetchScenarioMetadata({ params });
    },

    showAddScenarioDialog() {
      this.globalEmit('show-add-scenario-dialog');
    },

    onToggleFilteringByArchitectureGroup(eventData) {
      this.$emit('toggleFilteringByArchitectureGroup', eventData);
    },

    downloadCurrentView() {
      // Use ref to access the grid-view-container component and call its download method
      const gridViewContainer = this.$refs.gridViewContainer;

      if (gridViewContainer && gridViewContainer.downloadCurrentView) {
        gridViewContainer.downloadCurrentView();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

tr {
  background-color: white;
  border-bottom: solid 1px $grid-view-borders;
}
.add-scenario-row {
  background-color: white;
  font-size: 1.3rem;
  border-bottom: unset;

  td {
    padding: 0.4rem 0.7rem;
    vertical-align: middle;
    line-height: 1.7rem;
  }
}
.add-button {
  font-size: 1.3rem;
}

.alert-cell {
  background-color: $pricing-background !important;
  border-bottom: 0.1rem solid $pricing-background !important;
  min-width: $grid-view-alert-column-width !important;
}
</style>
